import React, { memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import Spinner from 'react-bootstrap/Spinner';
import { positionName, handedness } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useCachedOktaQuery, Team, Player, usePlayerGamesQuery } from '../data';
import PlayerHeadshot from './PlayerHeadshot';
import NavCardContainer from './NavCardContainer';
import NavCard from './NavCard';

const PlayerSelectorCards = ({ teamId, position, routePrefix, responsive, hideHeadshots, tabs }) => {
    const { t: translate } = useTranslation();
    const location = useLocation();
    const isBatter = position === 'batter';
    const extraQueryParams = window.$IsDugoutApp ? {} : { includeNonVisible: false };
    const linueupQueryKey = window.$IsDugoutApp ? 'dugout_lineup' : 'team_lineup';

    const extraRosterQueryParams = window.$IsDugoutApp ? {} : { username: 'team_roster_lineups' };
    const rosterQueryKey = window.$IsDugoutApp ? 'dugout_roster' : 'team_roster';

    const { series } = usePlayerGamesQuery();
    const isUpcomingSeries = series?.some(({ TeamID }) => TeamID.toString() === teamId.toString()) ?? false;
    const isOctoberOrLater = new Date().getMonth() >= 9;
    const shouldFetchLineup = isOctoberOrLater || isUpcomingSeries || window.$IsDugoutApp;

    const { data: teamRoster, status: teamRosterStatus } = useCachedOktaQuery(['teamRoster', teamId, position, rosterQueryKey],
        (t) => Player.list(t, { teamId, position, ...extraRosterQueryParams }), {}, 1000 * 60 * 5);

    const { data: lineup, status: lineupStatus, refetch: refetchLineup } = useCachedOktaQuery(
        ['lineup', teamId, position, linueupQueryKey],
        (t) => Team.lineup(t, teamId, isBatter, { ...extraQueryParams }),
        {
            initialData: [],
            staleTime: 1000 * 60 * 5,
            enabled: shouldFetchLineup,
        },
    );

    // forces refetch on hard refresh
    useEffect(() => {
        if (shouldFetchLineup) {
            refetchLineup();
        }
    }, [shouldFetchLineup, refetchLineup]);

    if (teamRosterStatus === 'loading' || lineupStatus === 'loading') {
        return <Spinner border="animation" />;
    }
    const getSubtitle = (player) => positionName(player?.Position || player.Player.PrimaryPosition, translate);
    const getHandedness = (player) => (isBatter ? handedness(player.Bats, false, translate) : handedness(player.Throws, false, translate));
    const getTabRoute = () => {
        const split = location?.pathname?.split('/') ?? [];
        if (split.length === 6) {
            const route = split[split.length - 1];
            if (tabs.map((t) => t.route).includes(route)) {
                return `/${route}`;
            }
            return `/${tabs[0].route}`;
        }
        return '';
    };
    const visibleLineup = lineup?.filter((player) => !(player.Visible === false && player.Player.RosterState !== 1))
        .sort((a, b) => (b.Visible ? 1 : 0) - (a.Visible ? 1 : 0)) || [];
    const roster = teamRoster
        ?.filter((r) => !visibleLineup.some((l) => l.Player.PlayerID === r.PlayerID))
        .map((r) => ({ Player: r })) || [];
    let players = [];

    if (isOctoberOrLater && !window.$IsDugoutApp) {
        // in October but never for Dugout: only show lineup
        players = visibleLineup;
    } else if (isUpcomingSeries || window.$IsDugoutApp) {
        // Before October and the team is in the upcoming series, or always for Dugout: lineup + roster
        players = [...visibleLineup, ...roster];
    } else {
        // Before October and NOT in the upcoming series: just show the roster
        players = roster;
    }

    players = players.map((player) => ({
        title: `${player.Player.FullName} (${getHandedness(player.Player)})`,
        subTitle: getSubtitle(player),
        route: `${routePrefix}${player.Player.PlayerID}${getTabRoute()}`,
        imgAs: () => (hideHeadshots ? <></> : <PlayerHeadshot playerId={player.Player.PlayerID} className="fa fa-fallback" />),
    }));

    return (
        <>
            {!!players && (
                <NavCardContainer>
                    {players.map((player) => (<NavCard key={player.route} {...player} {...responsive} />))}
                </NavCardContainer>
            )}
        </>
    );
};

PlayerSelectorCards.propTypes = {
    teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    position: PropTypes.string.isRequired,
    routePrefix: PropTypes.string,
    hideHeadshots: PropTypes.bool,
    responsive: PropTypes.shape({
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sm: PropTypes.number,
        md: PropTypes.number,
        lg: PropTypes.number,
        xl: PropTypes.number,
    }),
    tabs: PropTypes.arrayOf(PropTypes.object).isRequired,
};
PlayerSelectorCards.defaultProps = {
    routePrefix: '',
    responsive: undefined,
    hideHeadshots: false,
};

export default memo(PlayerSelectorCards, (prev, next) => prev.teamId === next.teamId
    && prev.routePrefix === next.routePrefix
    && prev.position === next.position);
